/**
 * P4 — report-branding editor (`/client-reports/branding`).
 *
 * Agency-wide report look-and-feel: a style preset, the 6 token colours (with a
 * live WCAG contrast hint), heading/body font stacks, logo/favicon uploads, and
 * a two-pane design-MD override editor with a live markdown preview. There is a
 * single branding record for the whole agency — branding is NOT configured
 * per-brand, so there is no brand selector. Every client report the agency
 * publishes inherits this one look (agency default → platform preset).
 *
 * Backend note: the branding record is the agency-default row (`shop_id` null,
 * `is_default` true). We read the list and edit that single row; creating one
 * POSTs `shop_slug: null` so central-api stores it as the agency default.
 *
 * Gated on `portal.branding.manage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ChromePicker, type ColorResult } from 'react-color';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { useCan } from '../../../lib/rbac';
import { setMessage } from '../../../store/reducers/appSlice';
import { HOME_ROUTE } from '../../../lib/constants/route';
import ClientReportsNav from '../ClientReportsNav';
import {
  listReportStylePresets,
  listReportBrandingWithResolved,
  createReportBranding,
  updateReportBranding,
  uploadBrandingAssets,
  type ReportStylePreset,
  type ReportBranding,
  type ResolvedReportBranding,
  type BrandingColors,
  type BrandingAssetKind,
} from '../../../lib/api/client-reports/report-branding-api';
import {
  COLOR_FIELDS,
  buildBrandingPatch,
  contrastRating,
  contrastRatio,
  draftFromBranding,
  draftFromResolved,
  emptyTokens,
  normaliseTokens,
  type BrandingDraft,
} from '../branding-helpers';
import '../client-reports.scss';

const ReportBrandingPage = () => {
  const dispatch = useDispatch();
  const canManage = useCan('portal.branding.manage');

  const [presets, setPresets] = useState<ReportStylePreset[]>([]);
  const [current, setCurrent] = useState<ReportBranding | null>(null);
  const [draft, setDraft] = useState<BrandingDraft>(() => ({
    preset_key: null,
    tokens: emptyTokens(),
    design_md_override: '',
  }));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<BrandingAssetKind | null>(null);
  const [openColor, setOpenColor] = useState<keyof BrandingColors | null>(null);

  const mdFileRef = useRef<HTMLInputElement>(null);

  // Load presets once.
  useEffect(() => {
    listReportStylePresets()
      .then(setPresets)
      .catch((err: any) =>
        dispatch(
          setMessage({
            message: err?.message || 'Failed to load style presets.',
            messageType: 'error',
          })
        )
      );
  }, [dispatch]);

  const presetByKey = useMemo(() => {
    const m = new Map<string, ReportStylePreset>();
    presets.forEach((p) => m.set(p.key, p));
    return m;
  }, [presets]);

  // Resolve the single agency-wide branding row out of the loaded list.
  const agencyDefaultRow = useCallback(
    (list: ReportBranding[]): ReportBranding | null =>
      list.find((b) => b.is_default || b.shop_id == null) ?? null,
    []
  );

  const seedDraft = useCallback(
    (row: ReportBranding | null, resolved: ResolvedReportBranding | null) => {
      if (row) {
        setDraft(draftFromBranding(row));
        return;
      }
      // No row yet — show the branding central-api resolves for the agency
      // (what reports use today).
      const fromResolved = draftFromResolved(resolved);
      if (fromResolved) {
        setDraft(fromResolved);
        return;
      }
      // Older backend without `resolved` — the first active preset's tokens.
      const preset = presets.find((p) => p.is_active) ?? presets[0];
      setDraft({
        preset_key: preset?.key ?? null,
        tokens: preset?.default_tokens
          ? normaliseTokens(preset.default_tokens)
          : emptyTokens(),
        design_md_override: '',
      });
    },
    [presets]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { rows, resolved } = await listReportBrandingWithResolved();
      const row = agencyDefaultRow(rows);
      setCurrent(row);
      seedDraft(row, resolved);
    } catch (err: any) {
      dispatch(
        setMessage({
          message: err?.message || 'Failed to load branding.',
          messageType: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [agencyDefaultRow, seedDraft, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // --- draft mutators ---
  const setColor = (key: keyof BrandingColors, hex: string) =>
    setDraft((d) => ({
      ...d,
      tokens: { ...d.tokens, colors: { ...d.tokens.colors, [key]: hex } },
    }));

  const setFont = (key: 'heading' | 'body', value: string) =>
    setDraft((d) => ({
      ...d,
      tokens: { ...d.tokens, fonts: { ...d.tokens.fonts, [key]: value } },
    }));

  const setMode = (mode: 'dark' | 'light') =>
    setDraft((d) => ({ ...d, tokens: { ...d.tokens, mode } }));

  const selectPreset = (key: string) => {
    const preset = presetByKey.get(key);
    setDraft((d) => ({
      ...d,
      preset_key: key,
      // Only re-seed tokens if the user hasn't got a row yet (fresh scope) so we
      // never silently overwrite hand-tuned colours on an existing brand.
      tokens:
        !current && preset?.default_tokens
          ? normaliseTokens(preset.default_tokens)
          : d.tokens,
    }));
  };

  // --- dirty tracking ---
  const patch = useMemo(
    () => buildBrandingPatch(current, draft),
    [current, draft]
  );
  // A scope with no row yet is always "creatable" once a preset is chosen.
  const isDirty = current ? patch !== null : Boolean(draft.preset_key);

  const handleSave = async () => {
    if (saving || !isDirty) return;
    setSaving(true);
    try {
      if (current) {
        if (patch) {
          const updated = await updateReportBranding(current.id, patch);
          applyRowUpdate(updated);
        }
      } else {
        // Agency-wide branding: always the agency-default row (shop_slug null).
        const created = await createReportBranding({
          preset_key: draft.preset_key,
          shop_slug: null,
          tokens: draft.tokens,
          design_md_override: draft.design_md_override.trim() || null,
        });
        applyRowUpdate(created);
      }
      dispatch(
        setMessage({ message: 'Branding saved.', messageType: 'success' })
      );
    } catch (err: any) {
      dispatch(
        setMessage({
          message: err?.message || 'Failed to save branding.',
          messageType: 'error',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  // Reset the working copy from the freshly saved row.
  const applyRowUpdate = (row: ReportBranding) => {
    setCurrent(row);
    setDraft(draftFromBranding(row));
  };

  const handleAssetPick = async (
    kind: BrandingAssetKind,
    file: File | undefined
  ) => {
    if (!file) return;
    if (!current) {
      dispatch(
        setMessage({
          message: 'Save the branding first, then upload logos.',
          messageType: 'info',
        })
      );
      return;
    }
    setUploading(kind);
    try {
      const updated = await uploadBrandingAssets(current.id, { [kind]: file });
      applyRowUpdate(updated);
      dispatch(
        setMessage({ message: 'Asset uploaded.', messageType: 'success' })
      );
    } catch (err: any) {
      dispatch(
        setMessage({
          message: err?.message || 'Failed to upload the asset.',
          messageType: 'error',
        })
      );
    } finally {
      setUploading(null);
    }
  };

  const handleMdUpload = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    setDraft((d) => ({ ...d, design_md_override: text }));
    if (mdFileRef.current) mdFileRef.current.value = '';
  };

  // WCAG hints for the most important pairings.
  const contrastPairs: Array<{ label: string; fg: string; bg: string }> = [
    {
      label: 'Text / background',
      fg: draft.tokens.colors.text,
      bg: draft.tokens.colors.bg,
    },
    {
      label: 'Muted / panel',
      fg: draft.tokens.colors.text_muted,
      bg: draft.tokens.colors.panel,
    },
    {
      label: 'Accent / background',
      fg: draft.tokens.colors.accent,
      bg: draft.tokens.colors.bg,
    },
  ];

  const assetPath = (kind: BrandingAssetKind): string | null => {
    if (!current) return null;
    if (kind === 'logo_light') return current.logo_light_path;
    if (kind === 'logo_dark') return current.logo_dark_path;
    if (kind === 'favicon') return current.favicon_path;
    return null;
  };

  if (!canManage) {
    return (
      <SubPageInnerContentHolder
        title='Report branding'
        backBtnLink={HOME_ROUTE}
      >
        <div className='client-reports__empty'>
          You don't have permission to manage report branding. Ask an owner or
          manager for the "manage branding" capability.
        </div>
      </SubPageInnerContentHolder>
    );
  }

  return (
    <SubPageInnerContentHolder title='Report branding' backBtnLink={HOME_ROUTE}>
      <ClientReportsNav />

      <div className='portal-branding'>
        {/* ---- header: agency-wide branding applies to every report ---- */}
        <div className='portal-branding__header'>
          <div className='portal-branding__scope-note'>
            Branding applies to your whole agency — every client report you
            publish uses this one look. There is no per-brand branding.
          </div>
          <div className='portal-branding__header-save'>
            <MainSquareButton
              type='button'
              value={saving ? 'Saving…' : 'Save branding'}
              buttonType='success'
              normal
              loading={saving}
              disabled={!isDirty || saving || loading}
              onClick={handleSave}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner customLoadingMsg='Loading branding…' />
          </div>
        ) : (
          <>
            {/* ---- (a) style preset ---- */}
            <section className='portal-branding__card'>
              <h3 className='portal-branding__card-title'>Style preset</h3>
              <p className='portal-branding__card-hint'>
                Pick a base design; your logo, colours and fonts overlay it.
              </p>
              {presets.length === 0 ? (
                <div className='portal-branding__muted'>
                  No presets available yet.
                </div>
              ) : (
                <div className='portal-branding__presets'>
                  {presets.map((p) => (
                    <button
                      key={p.key}
                      type='button'
                      className={`portal-branding__preset${
                        draft.preset_key === p.key
                          ? ' portal-branding__preset--active'
                          : ''
                      }`}
                      onClick={() => selectPreset(p.key)}
                    >
                      <span className='portal-branding__preset-thumb'>
                        {p.preview_thumbnail_path ? (
                          <img
                            src={p.preview_thumbnail_path}
                            alt={p.name}
                            onError={(e) => {
                              (
                                e.currentTarget as HTMLImageElement
                              ).style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className='portal-branding__preset-thumb-fallback'>
                            {p.name.slice(0, 1)}
                          </span>
                        )}
                      </span>
                      <span className='portal-branding__preset-name'>
                        {p.name}
                      </span>
                      {p.description && (
                        <span className='portal-branding__preset-desc'>
                          {p.description}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* ---- (b) colours ---- */}
            <section className='portal-branding__card'>
              <div className='portal-branding__card-head'>
                <div>
                  <h3 className='portal-branding__card-title'>Colours</h3>
                  <p className='portal-branding__card-hint'>
                    The report CSS-variable palette.
                  </p>
                </div>
                <div className='portal-branding__mode'>
                  <button
                    type='button'
                    className={`portal-branding__mode-btn${
                      draft.tokens.mode === 'dark'
                        ? ' portal-branding__mode-btn--active'
                        : ''
                    }`}
                    onClick={() => setMode('dark')}
                  >
                    Dark
                  </button>
                  <button
                    type='button'
                    className={`portal-branding__mode-btn${
                      draft.tokens.mode === 'light'
                        ? ' portal-branding__mode-btn--active'
                        : ''
                    }`}
                    onClick={() => setMode('light')}
                  >
                    Light
                  </button>
                </div>
              </div>

              <div className='portal-branding__colors'>
                {COLOR_FIELDS.map((f) => {
                  const hex = draft.tokens.colors[f.key];
                  return (
                    <div key={f.key} className='portal-branding__color'>
                      <span className='portal-branding__color-label'>
                        {f.label}
                      </span>
                      <div className='portal-branding__color-row'>
                        <button
                          type='button'
                          className='portal-branding__swatch'
                          style={{ backgroundColor: hex }}
                          aria-label={`Pick ${f.label}`}
                          onClick={() =>
                            setOpenColor(openColor === f.key ? null : f.key)
                          }
                        />
                        <input
                          className='portal-branding__hex'
                          value={hex}
                          onChange={(e) => setColor(f.key, e.target.value)}
                        />
                      </div>
                      {openColor === f.key && (
                        <div className='portal-branding__picker'>
                          <div
                            className='portal-branding__picker-backdrop'
                            onClick={() => setOpenColor(null)}
                          />
                          <div className='portal-branding__picker-pop'>
                            <ChromePicker
                              color={hex}
                              disableAlpha
                              onChange={(c: ColorResult) =>
                                setColor(f.key, c.hex)
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className='portal-branding__contrast'>
                {contrastPairs.map((cp) => {
                  const ratio = contrastRatio(cp.fg, cp.bg);
                  const rating = contrastRating(ratio);
                  return (
                    <span
                      key={cp.label}
                      className={`portal-branding__contrast-chip portal-branding__contrast-chip--${
                        rating === 'Fail' ? 'fail' : 'ok'
                      }`}
                    >
                      {cp.label}: {ratio ? ratio.toFixed(1) : '—'} ({rating})
                    </span>
                  );
                })}
              </div>
            </section>

            {/* ---- (c) fonts ---- */}
            <section className='portal-branding__card'>
              <h3 className='portal-branding__card-title'>Fonts</h3>
              <p className='portal-branding__card-hint'>
                CSS font stacks used for report headings and body text.
              </p>
              <div className='portal-branding__field'>
                <label className='portal-branding__label'>Heading font</label>
                <input
                  className='portal-branding__input'
                  value={draft.tokens.fonts.heading}
                  onChange={(e) => setFont('heading', e.target.value)}
                  placeholder='Mona Sans, system-ui, sans-serif'
                />
              </div>
              <div className='portal-branding__field'>
                <label className='portal-branding__label'>Body font</label>
                <input
                  className='portal-branding__input'
                  value={draft.tokens.fonts.body}
                  onChange={(e) => setFont('body', e.target.value)}
                  placeholder='Mona Sans, system-ui, sans-serif'
                />
              </div>
            </section>

            {/* ---- (d) logos ---- */}
            <section className='portal-branding__card'>
              <h3 className='portal-branding__card-title'>Logos & favicon</h3>
              <p className='portal-branding__card-hint'>
                {current
                  ? 'PNG/SVG recommended. Uploads apply immediately.'
                  : 'Save the branding first to enable uploads.'}
              </p>
              <div className='portal-branding__assets'>
                {(
                  [
                    { kind: 'logo_light', label: 'Logo (light bg)' },
                    { kind: 'logo_dark', label: 'Logo (dark bg)' },
                    { kind: 'favicon', label: 'Favicon' },
                  ] as Array<{ kind: BrandingAssetKind; label: string }>
                ).map(({ kind, label }) => {
                  const path = assetPath(kind);
                  return (
                    <div key={kind} className='portal-branding__asset'>
                      <span className='portal-branding__label'>{label}</span>
                      <div className='portal-branding__asset-state'>
                        {path ? (
                          <span className='portal-branding__asset-set'>
                            ✓ uploaded
                          </span>
                        ) : (
                          <span className='portal-branding__asset-unset'>
                            none
                          </span>
                        )}
                      </div>
                      <label className='portal-branding__file-btn'>
                        {uploading === kind ? 'Uploading…' : 'Choose file'}
                        <input
                          type='file'
                          accept={
                            kind === 'favicon'
                              ? 'image/png,image/x-icon,image/svg+xml'
                              : 'image/png,image/svg+xml,image/jpeg'
                          }
                          hidden
                          disabled={!current || uploading !== null}
                          onChange={(e) =>
                            handleAssetPick(kind, e.target.files?.[0])
                          }
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ---- (e) design MD editor ---- */}
            <section className='portal-branding__card'>
              <div className='portal-branding__card-head'>
                <div>
                  <h3 className='portal-branding__card-title'>
                    Design instructions (Markdown)
                  </h3>
                  <p className='portal-branding__card-hint'>
                    Optional overrides/extension of the preset's design system —
                    tone of voice, layout notes, do's & don'ts. The assistant
                    reads this when authoring reports.
                  </p>
                </div>
                <label className='portal-branding__file-btn'>
                  Upload .md
                  <input
                    ref={mdFileRef}
                    type='file'
                    accept='.md,text/markdown,text/plain'
                    hidden
                    onChange={(e) => handleMdUpload(e.target.files?.[0])}
                  />
                </label>
              </div>
              <div className='portal-branding__md'>
                <textarea
                  className='portal-branding__md-editor'
                  value={draft.design_md_override}
                  placeholder='# Brand voice&#10;- Confident, concise, data-led…'
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      design_md_override: e.target.value,
                    }))
                  }
                />
                <div className='portal-branding__md-preview'>
                  {draft.design_md_override.trim() ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {draft.design_md_override}
                    </ReactMarkdown>
                  ) : (
                    <span className='portal-branding__muted'>
                      Preview appears here.
                    </span>
                  )}
                </div>
              </div>
            </section>

            <p className='portal-branding__footnote'>
              Preview: these tokens compile to the report's CSS variables and
              render live on the client portal when a report is published.
            </p>
          </>
        )}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default ReportBrandingPage;
